'use client';

import { useRef } from 'react';
import { Navigation } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { motion } from 'motion/react';
import WestRoundedIcon from '@mui/icons-material/WestRounded';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import type { ReactNode } from 'react';
import { EASE, DURATION, VIEWPORT } from '@/components/animations/tokens';
import 'swiper/css';
import 'swiper/css/navigation';

interface CarouselProps {
  children: ReactNode[];
  spaceBetween?: number;
  breakpoints?: {
    [key: number]: {
      slidesPerView: number;
    };
  };
}

export default function Carousel({
  children,
  spaceBetween = 16,
  breakpoints = {
    0: {
      slidesPerView: 1,
    },
    900: {
      slidesPerView: 2,
    },
    1200: {
      slidesPerView: 3,
    },
  },
}: CarouselProps) {
  const swiperRef = useRef<SwiperType>(null);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
        }}
      >
        <IconButton
          onClick={() => swiperRef.current?.slidePrev()}
          color='primary'
          aria-label='Previous slide'
          sx={{
            bgcolor: 'background.paper',
            boxShadow: 2,
            '&:hover': {
              bgcolor: 'background.paper',
            },
          }}
        >
          <WestRoundedIcon />
        </IconButton>
      </Box>

      <Swiper
        modules={[Navigation]}
        spaceBetween={spaceBetween}
        breakpoints={breakpoints}
        loop
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
      >
        {children.map((child, index) => (
          <SwiperSlide key={index}>
            <motion.article
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: VIEWPORT.once, amount: VIEWPORT.amount, margin: VIEWPORT.margin }}
              transition={{
                duration: DURATION.base,
                ease: EASE.standard,
                delay: Math.min(index * 0.1, 0.4),
              }}
              style={{ height: '100%' }}
            >
              {child}
            </motion.article>
          </SwiperSlide>
        ))}
      </Swiper>

      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translate(50%, -50%)',
          zIndex: 2,
        }}
      >
        <IconButton
          onClick={() => swiperRef.current?.slideNext()}
          color='primary'
          aria-label='Next slide'
          sx={{
            bgcolor: 'background.paper',
            boxShadow: 2,
            '&:hover': {
              bgcolor: 'background.paper',
            },
          }}
        >
          <EastRoundedIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
